import DisplayHelper from './display_helper';
import Constants from './constants';

export default class FinancingHelper {
  static getProductTypeTitle(financingMethod) {
    let alias;
    switch (financingMethod.financingProduct.type) {
      case 'ppa':
        alias = 'PPA';
        break;
      case 'loan':
        if (financingMethod.financingProduct.productSku.substring(0, 5) === 'SLOAN') {
          alias = 'Solar Loan';
        }
        break;
      case 'hvac':
        if (financingMethod.financingProduct.productSku.includes('HVAC')) {
          alias = 'HVAC Loan';
        }
        break;
      case 'battery':
        if (financingMethod.financingProduct.productSku.includes('BATTERY')) {
          alias = 'Battery Loan';
        }
        break;
      case 'cash':
        if (financingMethod.financingProduct.productSku.substring(0, 4) === 'CASH') {
          alias = 'Cash';
        }
        break;
      default:
        break;
    }
    return alias;
  }
  static getProductType(financingMethod) {
    let productType;
    if (financingMethod.financingProduct) {
      productType = financingMethod.financingProduct.type;
    } else if (financingMethod.productType) {
      productType = financingMethod.productType;
    }
    return productType;
  }
  static getProductSku(financingMethod) {
    let productSku;
    if (financingMethod.financingProduct) {
      productSku = financingMethod.financingProduct.productSku;
    } else if (financingMethod.productSku) {
      productSku = financingMethod.productSku;
    }
    return productSku;
  }

  static isOnRetiredRateCard(financingMethod) {
    return !!financingMethod?.retiredFinancingCalculationModelId;
  }

  static getProductElement(financingMethod, elementName) {
    const {
      financingProduct: { elements },
    } = financingMethod;
    if (elements) {
      return elements.find(({ name }) => name === elementName);
    }
    return null;
  }

  static getTaxBrackets(financingMethod) {
    let taxBrackets = [];

    const productElement = this.getProductElement(financingMethod, 'tax_bracket');
    if (productElement) {
      const valueList = JSON.parse(productElement.valueList);
      let selected, taxBracket, taxRate;
      for (taxRate in valueList) {
        if (valueList.hasOwnProperty(taxRate)) {
          taxBracket = valueList[taxRate];
          selected = productElement.defaultValue === taxRate ? true : false;
          taxBrackets.push({
            selected,
            taxBracket,
            taxRate,
          });
        }
      }
    }
    return taxBrackets;
  }

  static isHicRequired(elements) {
    const hicUploadRequiredElement = elements && elements.find(({ name }) => name === 'hic_upload_required');
    return (
      hicUploadRequiredElement !== undefined &&
      hicUploadRequiredElement &&
      hicUploadRequiredElement.defaultValue === '1'
    );
  }

  static loanTypeLabel(data, loanTypeOptions, selectedLoanType) {
    let groupName = data?.calculationModel?.groupName ?? data?.groupName;
    if (groupName) {
      let loanType = '';
      if ((data?.loanTypeCode || data?.loanType)?.toLowerCase()?.includes('level')) {
        loanType = 'Level ';
      } else if ((data?.loanTypeCode || data?.loanType)?.toLowerCase()?.includes('standard')) {
        loanType = 'Standard ';
      }

      groupName = `${loanType}${groupName}${data?.loanTypeLabel?.includes('*') ? '*' : ''}`;
    }
    return (
      groupName ??
      data?.calculationModel?.loanTypeEntity?.label ??
      data?.loanTypeLabel ??
      loanTypeOptions?.find((loanType) => loanType.code === selectedLoanType)?.label ??
      selectedLoanType
    );
  }

  static getLoanInformation(data, loanTypeOptions) {
    if (FinancingHelper.isCashProduct(data)) {
      return 'Cash';
    }

    return `${this.loanTypeLabel(data, loanTypeOptions, data.loanType || data.loanTypeCode)} - ${
      data.deferralMonths || data.calculations?.deferralMonth || '0'
    } Mo | ${data.term} Yr | ${DisplayHelper.formatAPR(data.apr)}`;
  }

  static getLoanInformationV2(data, loanTypeOptions, rows) {
    const FILL_SPACE = ' ';

    function getLoanTypeLabel(data, spacing) {
      return FinancingHelper.loanTypeLabel(data, loanTypeOptions, data.loanType || data.loanTypeCode).padEnd(
        spacing,
        FILL_SPACE
      );
    }

    function getDeferral(data, spacing) {
      return `${data.deferralMonths || data.calculations?.deferralMonth || '0'} Mo`.padStart(spacing, FILL_SPACE);
    }

    function getTerm(data, spacing) {
      return `${data.term} Yr`.padStart(spacing, FILL_SPACE);
    }

    function getApr(data, spacing) {
      return DisplayHelper.formatAPR(data.apr).padStart(spacing, FILL_SPACE);
    }

    let spacings = rows.reduce(
      (acc, row) => {
        return {
          loanType: Math.max(acc.loanType, getLoanTypeLabel(row, 0).length),
          deferral: Math.max(acc.deferral, getDeferral(row, 0).length),
          term: Math.max(acc.term, getTerm(row, 0).length),
          apr: Math.max(acc.apr, getApr(row, 0).length),
        };
      },
      {
        loanType: 0,
        deferral: 0,
        term: 0,
        apr: 0,
      }
    );

    if (data?.loanTypeCode?.toLowerCase()?.includes('cash')) {
      return 'Cash';
    }
    // takes into account all rows for equal spacing
    return `${getLoanTypeLabel(data, spacings.loanType)} - ${getDeferral(data, spacings.deferral)} | ${getTerm(
      data,
      spacings.term
    )} | ${getApr(data, spacings.apr)}`;
  }

  static getSelectedRateCard(financingMethod, rateCards) {
    if (!financingMethod) {
      return null;
    }
    return rateCards.find(({ term, apr, loanTypeCode, deferralMonths }) => {
      const dMth =
        financingMethod.deferralMonth ?? financingMethod.deferralMonths ?? financingMethod.calculations?.deferralMonth;
      return (
        financingMethod.apr === apr &&
        financingMethod.loanType === loanTypeCode &&
        financingMethod.term === term &&
        dMth === deferralMonths
      );
    });
  }

  static getRateCardsFromEligibility(eligibility, financingMethod) {
    //do this more sane
    if (!eligibility?.creditCheck?.financingProducts?.length) {
      return [];
    }

    let selectedProduct =
      eligibility.creditCheck.financingProducts?.find(
        ({ financingProductSku: sku, financingProductType }) =>
          (sku && sku === financingMethod?.productSku) ||
          (financingProductType && financingProductType === financingMethod?.productType)
      ) || eligibility.creditCheck.financingProducts[0];

    // console.log('productSku', financingMethod?.productSku, 'selectedProduct', selectedProduct);

    return selectedProduct?.rateCards || [];
  }

  static rateCardsByProduct(eligibility) {
    if (!eligibility?.creditCheck?.financingProducts?.length) {
      return {};
    }

    return eligibility.creditCheck.financingProducts?.reduce((acc, fp) => {
      acc[fp.financingProductSku] = fp.rateCards;
      return acc;
    }, {});
  }

  static areStipsRequired(rateCard, reqId = null) {
    if (!rateCard.stips || !rateCard.stips.length) {
      return false;
    }
    return rateCard.stips.some(
      ({ reqId: stipReqId, isRequired, status }) =>
        (reqId === null || stipReqId === reqId) && isRequired && status !== 'Accepted'
    );
  }

  static sortRateCards(rateCards) {
    return rateCards.sort(
      (a, b) =>
        (a.groupName ?? '')?.localeCompare(b.groupName ?? '') ||
        a.loanTypeSequenceId - b.loanTypeSequenceId ||
        a.term - b.term ||
        a.apr - b.apr ||
        a.id - b.id
    );
  }

  static haveAllStipsBeenAccepted(rateCard, reqId) {
    // If there are stips, check to ensure they are all accepted.
    // If there aren't stips, then treat as being accepted, unless it is pended.
    // (pended rate cards don't currently return stips, even though they may be stipped,
    //  but soon, pended will return stips in some cases.  In cases they don't return stips, treat as not accepted.
    if (rateCard.stips?.length > 0) {
      return rateCard.stips.every(({ reqId: stipReqId, status }) => {
        return (reqId !== null && stipReqId !== reqId) || status === 'Accepted';
      });
    }
    return rateCard.decision !== 'pended';
  }

  static rateCardIsGoodForLoan(selectedRateCard, financingMethod) {
    // loan must be covered
    // rate card must be 'approved'
    // stips must be 'accepted'

    let checkAmountFinancedValidity = FinancingHelper.checkAmountFinancedValidity(
      financingMethod.amountFinanced,
      selectedRateCard,
      financingMethod
    );
    return (
      checkAmountFinancedValidity.valid &&
      checkAmountFinancedValidity.validVsUnverified &&
      selectedRateCard.decision === 'approved' &&
      (!selectedRateCard.stips ||
        selectedRateCard.stips.length === 0 ||
        selectedRateCard.stips.every(({ reqId, status, isRequired }) => {
          return reqId !== 'VOI' || status === 'Accepted' || !isRequired;
        }))
    );
  }

  static checkAmountFinancedValidity(amountFinanced, selectedRateCard, financingMethod) {
    let valid = false;
    let validVsUnverified = false;
    let validVsVerified = false;
    let validationMessage = '';
    const formatMoney = DisplayHelper.formatMoneyComma;

    // console.log('amountFinanced', amountFinanced, 'selectedRateCard', selectedRateCard, 'financingMethod', financingMethod);
    if (selectedRateCard && amountFinanced > selectedRateCard.maxApprovedUnverified) {
      // but lower than the verified max
      if (selectedRateCard.maxApprovedVerified == null || amountFinanced <= selectedRateCard.maxApprovedVerified) {
        validVsVerified = true;
        // Let them continue if they are under the verified max and either have their stips accepted, or if all stips are required
        // Once we add the ability to continue with optional stips (US521), we'll need to modify this logic.  Maybe just make valid = true.
        valid = true;
      } else {
        validationMessage = `Max amount approved: ${formatMoney(selectedRateCard.maxApprovedVerified)}`;
      }
    }
    // too high
    else if (amountFinanced > Constants.MAX_LOAN_AMOUNT) {
      validationMessage = `Maximum Amount: ${formatMoney(Constants.MAX_LOAN_AMOUNT)}`;
    } else if (amountFinanced === 0) {
      validationMessage = `Amount cannot be zero`;
    } else if (
      (selectedRateCard && selectedRateCard.minLoanAmount
        ? selectedRateCard.minLoanAmount
        : financingMethod?.minimumAmountFinanced) > amountFinanced
    ) {
      validationMessage = `Minimum amount: ${formatMoney(
        selectedRateCard && selectedRateCard.minLoanAmount
          ? selectedRateCard.minLoanAmount
          : financingMethod?.minimumAmountFinanced
      )}`;
    }
    // too low
    else {
      validVsUnverified = true;
      validVsVerified = true;
      valid = true;
    }

    return { valid, validVsUnverified, validVsVerified, validationMessage };
  }

  static rateCardIsGoodForLoanV2(eligibility, financingMethod) {
    let rateCards = FinancingHelper.getRateCardsFromEligibility(eligibility, financingMethod);
    let selectedRateCard = FinancingHelper.getSelectedRateCard(financingMethod, rateCards);

    let isValid = FinancingHelper.checkAmountFinancedValidity(
      financingMethod.amountFinanced,
      selectedRateCard,
      financingMethod
    );

    if (!(isValid.valid || isValid.validVsVerified || isValid.validVsUnverified) || isValid.validationMessage) {
      return { error: isValid.validationMessage, ...isValid };
    }

    if (selectedRateCard?.decision !== 'approved') {
      return { error: 'rate card is not approved', ...isValid };
    }

    return {
      ok:
        isValid.valid &&
        isValid.validVsUnverified &&
        selectedRateCard?.decision === 'approved' &&
        (!selectedRateCard?.stips ||
          selectedRateCard?.stips.length === 0 ||
          selectedRateCard?.stips?.every(({ reqId, status, isRequired }) => {
            return reqId !== 'VOI' || status === 'Accepted' || !isRequired;
          })),
      ...isValid,
    };
  }

  static eligibilityRateCardsCanFinanceLoan({ eligibility, financingMethod }) {
    if (!eligibility || !financingMethod) {
      return false;
    }
    let rateCards = FinancingHelper.getRateCardsFromEligibility(eligibility, financingMethod);

    let selectedRateCard = FinancingHelper.getSelectedRateCard(financingMethod, rateCards);

    return FinancingHelper.rateCardIsGoodForLoan(selectedRateCard, financingMethod);
  }

  static cantGetLoan(rateCards) {
    return rateCards.every(
      ({ maxApprovedUnverified, maxApprovedVerified }) => !maxApprovedUnverified && !maxApprovedVerified
    );
  }

  static isComplete(rateCards) {
    return (
      rateCards?.every((rateCard) => {
        let stips = rateCard.stips || [];
        if (
          rateCard.decision === 'approved' &&
          (stips.length === 0 ||
            stips.every(({ status }) => status === 'Accepted') ||
            stips.every(({ reqId, isRequired }) => reqId !== 'VOI' && !isRequired))
        ) {
          return true;
        } else if (rateCard.decision === 'failed') {
          return true;
        } else if (FinancingHelper.areStipsRequired(rateCard)) {
          return false;
        } else if (stips.some(({ reqId }) => reqId === 'VOI')) {
          return false;
        } else {
          return true;
        }
      }) || false
    );
  }

  static formatDeferralMonths(row) {
    // if(row.deferralMonths) {
    return `${row.deferralMonths} Mo`;
    // }
    // return ''
  }

  static isCashProduct = (fm) => this.getProductTypeTitle(fm) === 'Cash';
  static isLoanProduct = (fm) => this.getProductTypeTitle(fm) === 'Loan';
  static isCash = (fm) => fm.lenderCode.includes('cash');
  static isMosaic = (fm) => fm.lenderCode.includes('mosaic');
  static isMedallion = (fm) => fm.lenderCode.includes('medallion');
  static isTribeam = (fm) => fm.lenderCode.includes('medallion');
}
